import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/features/auth/AuthProvider";

/**
 * Shared staff record hook — used by BOTH staff and admin shells to resolve
 * the currently-authenticated user's staff profile (name, avatar, check-in
 * eligibility).
 *
 * IMPORTANT: Do NOT add `hourly_rate` or any pay-related column to this
 * selection. Admins often apply deductions (travel, advances, gear) that make
 * the raw rate misleading if staff see it. Pay-related fields must be pulled
 * only by admin-scoped hooks (see `src/features/admin/staff.ts`) and never
 * land in the staff-side React Query cache.
 */
export interface StaffProfile {
  id: string;
  email: string;
  name: string;
  user_level: string | null;
  avatar_url: string | null;
  must_change_password: boolean;
}

export function useStaff(opts?: { enabled?: boolean }) {
  const { user } = useAuth();
  const enabled = (opts?.enabled ?? true) && Boolean(user?.id);

  return useQuery({
    queryKey: ["staff", user?.id ?? null],
    enabled,
    queryFn: async (): Promise<StaffProfile | null> => {
      const { data, error } = await supabase
        .from("staff")
        .select("id, email, name, user_level, avatar_url, must_change_password")
        .eq("user_id", user!.id)
        .maybeSingle();
      if (error) throw error;
      return data as StaffProfile | null;
    },
  });
}
